import React from "react";
import PropTypes from "prop-types";

// Yhteinen pohjatyyli kaikille nappuloille.
// Pidetään kevyenä: ei raskaita glow-varjoja eikä borderin lisäystä hoverissa,
// jotta layout ei hypi.
const baseButtonClass =
  "inline-flex justify-center items-center gap-2 " +
  "font-customFont1 rounded-full " +
  "transition-all duration-200 ease-out " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 " +
  "dark:focus-visible:ring-offset-darkpage " +
  "active:scale-[0.98]";

// Eri tyylivariantit
const variantStyles = {
  default: {
    className:
      "min-h-11 px-6 text-sm sm:text-base font-semibold " +
      "bg-text text-white border border-text shadow-sm " +
      "hover:bg-darkest hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darktext dark:text-darkpage dark:border-darktext " +
      "dark:hover:bg-white",
    style: {},
  },

  hero: {
    className:
      "min-h-12 sm:min-h-14 px-8 sm:px-10 " +
      "text-sm sm:text-base font-bold tracking-wide " +
      "bg-primary text-darkest border border-primary shadow-sm " +
      "hover:bg-accent hover:border-accent hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-primary dark:text-darkest dark:border-primary " +
      "dark:hover:bg-accent dark:hover:border-accent",
    style: {},
  },

  seemore: {
    className:
      "min-h-12 sm:min-h-14 px-8 sm:px-10 w-44 sm:w-52 md:w-60 " +
      "text-sm sm:text-base md:text-lg font-bold tracking-wide " +
      "bg-text text-white border border-text shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darktext dark:text-darkpage dark:border-darktext " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  servicesreadmore: {
    className:
      "min-h-11 px-6 text-sm sm:text-base font-bold " +
      "bg-card text-text border border-border shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darkcard dark:text-darktext dark:border-darkborder " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  backbutton: {
    className:
      "min-h-11 px-5 sm:px-6 " +
      "text-sm sm:text-base font-bold " +
      "bg-surface text-text border border-border shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darksurface dark:text-darktext dark:border-darkborder " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  addtocart: {
    className:
      "w-full md:w-auto min-h-11 px-5 " +
      "text-sm md:text-base font-semibold " +
      "bg-emerald-500 text-white border border-emerald-500 shadow-sm " +
      "hover:bg-emerald-600 hover:border-emerald-600 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  removefromcart: {
    className:
      "w-full md:w-auto min-h-11 px-5 " +
      "text-sm md:text-base font-semibold " +
      "bg-red-600 text-white border border-red-600 shadow-sm " +
      "hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  makeabooking: {
    className:
      "w-full md:w-auto min-h-11 px-6 " +
      "text-sm md:text-base font-bold " +
      "bg-primary text-darkest border border-primary shadow-sm " +
      "hover:bg-accent hover:border-accent hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  scrolltotop: {
    className:
      "h-11 w-11 px-0 text-sm font-bold " +
      "bg-text text-white border border-text shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darktext dark:text-darkpage dark:border-darktext " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  proceedtocheckout: {
    className:
      "w-full min-h-11 px-5 py-2 " +
      "text-sm font-extrabold tracking-wide uppercase " +
      "bg-primary text-darkest border border-primary shadow-sm " +
      "hover:bg-accent hover:border-accent hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  productcardaddtocart: {
    className:
      "w-full xxxsm:w-24 xsm:w-28 sm:w-32 md:w-36 lg:w-40 xl:w-48 h-9 " +
      "text-[12px] font-semibold " +
      "bg-emerald-500 text-white border border-emerald-500 shadow-sm " +
      "hover:bg-emerald-600 hover:border-emerald-600 hover:-translate-y-0.5",
    style: {},
  },

  productcardremovefromcart: {
    className:
      "w-full xxxsm:w-24 xsm:w-28 sm:w-32 md:w-36 lg:w-40 xl:w-48 h-9 " +
      "text-[12px] font-semibold " +
      "bg-red-600 text-white border border-red-600 shadow-sm " +
      "hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5",
    style: {},
  },

  productcarddetails: {
    className:
      "w-full xxxsm:w-24 xsm:w-28 sm:w-32 md:w-36 lg:w-40 xl:w-48 h-9 " +
      "text-[12px] font-semibold " +
      "bg-surface text-text border border-border shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 " +
      "dark:bg-darksurface dark:text-darktext dark:border-darkborder " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  closeform: {
    className:
      "min-h-11 px-6 mt-2 " +
      "text-sm sm:text-base font-semibold tracking-wide " +
      "bg-red-600 text-white border border-red-600 shadow-sm " +
      "hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  modalbutton: {
    className:
      "w-full sm:w-48 min-h-12 px-6 py-3 " +
      "text-base sm:text-lg font-semibold " +
      "bg-primary text-darkest border border-primary shadow-sm " +
      "hover:bg-accent hover:border-accent hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  artistcardbutton: {
    className:
      "min-h-10 sm:min-h-11 md:min-h-12 w-36 sm:w-40 md:w-44 px-5 " +
      "text-sm font-semibold tracking-wide " +
      "bg-primary text-darkest border border-primary shadow-sm " +
      "hover:bg-accent hover:border-accent hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  askwithform: {
    className:
      "min-h-10 sm:min-h-11 md:min-h-12 w-36 sm:w-40 md:w-44 px-5 " +
      "text-sm sm:text-base font-semibold tracking-wide " +
      "bg-text text-white border border-text shadow-sm " +
      "hover:bg-primary hover:text-darkest hover:border-primary hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darktext dark:text-darkpage dark:border-darktext " +
      "dark:hover:bg-primary dark:hover:text-darkest dark:hover:border-primary",
    style: {},
  },

  panelSave: {
    className:
      "px-6 py-2 text-sm font-semibold tracking-wide " +
      "bg-cyan-500 text-white border border-cyan-500 shadow-sm " +
      "hover:bg-cyan-600 hover:border-cyan-600 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  panelPublish: {
    className:
      "px-6 py-2 text-sm font-semibold tracking-wide " +
      "bg-emerald-500 text-white border border-emerald-500 shadow-sm " +
      "hover:bg-emerald-600 hover:border-emerald-600 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  panelLogout: {
    className:
      "px-6 py-2 text-sm font-semibold tracking-wide " +
      "bg-rose-600 text-white border border-rose-600 shadow-sm " +
      "hover:bg-rose-700 hover:border-rose-700 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  panelPreview: {
    className:
      "px-6 py-2 text-sm font-semibold tracking-wide " +
      "bg-amber-400 text-darkest border border-amber-400 shadow-sm " +
      "hover:bg-amber-500 hover:border-amber-500 hover:-translate-y-0.5 hover:shadow-md",
    style: {},
  },

  panelCancel: {
    className:
      "px-6 py-2 text-sm font-semibold tracking-wide " +
      "bg-slate-200 text-darkest border border-slate-300 shadow-sm " +
      "hover:bg-slate-300 hover:border-slate-400 hover:-translate-y-0.5 hover:shadow-md " +
      "dark:bg-darksurface dark:text-darktext dark:border-darkborder " +
      "dark:hover:bg-darksurface2",
    style: {},
  },
};

const ResponsiveButton = ({
  onClick,
  children,
  icon: Icon,
  className = "",
  style = {},
  variant = "default",
  disabled = false,
  ...props
}) => {
  const variantData = variantStyles[variant] || variantStyles.default;
  const mergedStyle = { ...variantData.style, ...style };
  const variantClass = variantData.className;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseButtonClass}
        ${variantClass}
        ${className}
        ${
          disabled
            ? "opacity-50 cursor-not-allowed pointer-events-none"
            : "cursor-pointer"
        }
      `}
      style={mergedStyle}
      {...props}
    >
      {Icon && <Icon className="shrink-0 text-sm sm:text-base" />}
      <span className="font-customFont1 truncate">{children}</span>
    </button>
  );
};

ResponsiveButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  className: PropTypes.string,
  style: PropTypes.object,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ResponsiveButton;